/**
 * サロンボード互換機能のユーティリティ関数
 */

import { 
  ReservationNumberPrefix,
  ReservationSource,
  DetailedReservationStatus,
  VisitProcessingStatus,
  ExtendedReservation,
  ExtendedCustomer,
  calculateVisitProcessingDeadline,
  calculateCancellationFee
} from '../types/salonboard';
import { getSequences, updateSequence } from './localStorage';

/**
 * 予約番号生成
 * @param prefix 予約番号のプレフィックス
 * @param number シーケンス番号
 * @returns 予約番号（例: BD00000001）
 */
export function generateReservationNumber(prefix: string, number: number): string {
  return `${prefix}${number.toString().padStart(8, '0')}`;
}

/**
 * 顧客コード生成
 * @param number シーケンス番号
 * @returns 顧客コード（例: C0000001）
 */
export function generateCustomerCode(number: number): string {
  return `C${number.toString().padStart(7, '0')}`;
}

/**
 * シーケンス番号管理クラス
 * LocalStorageでシーケンス番号を永続化
 */
export class SequenceManager {
  private static instance: SequenceManager;
  
  private constructor() {}
  
  static getInstance(): SequenceManager {
    if (!this.instance) {
      this.instance = new SequenceManager();
    }
    return this.instance;
  }
  
  /**
   * 次の予約番号を生成
   * @param prefix 予約番号のプレフィックス
   * @returns 予約番号（例: BD00000001）
   */
  getNextReservationNumber(prefix: ReservationNumberPrefix): string {
    const sequences = getSequences();
    const key = `reservation_${prefix}` as keyof typeof sequences;
    const current = sequences[key] || 0;
    const next = current + 1;
    
    updateSequence(key, next);
    
    return generateReservationNumber(prefix, next);
  }
  
  /**
   * 次の顧客コードを生成
   * @returns 顧客コード（例: C0000001）
   */
  getNextCustomerCode(): string {
    const sequences = getSequences();
    const current = sequences.customer || 0;
    const next = current + 1;
    
    updateSequence('customer', next);
    
    return generateCustomerCode(next);
  }
}

/**
 * 予約経路から予約番号のプレフィックスを決定
 * @param source 予約経路
 * @returns 予約番号プレフィックス
 */
export function determinePrefix(source: ReservationSource): ReservationNumberPrefix {
  switch (source) {
    case 'HOT_PEPPER_BEAUTY':
      return 'BD';
    case 'PHONE':
      return 'YE';
    case 'WEBSITE':
      return 'WB';
    case 'OTHER':
    case 'WALK_IN':
    default:
      return 'OT';
  }
}

/**
 * 旧ステータスを詳細ステータスにマッピング
 * @param oldStatus 旧ステータス
 * @returns 詳細ステータス
 */
export function mapOldStatusToDetailed(
  oldStatus: 'confirmed' | 'cancelled' | 'completed'
): DetailedReservationStatus {
  switch (oldStatus) {
    case 'confirmed':
      return 'WAITING_RECEPTION';
    case 'cancelled':
      return 'CUSTOMER_CANCELLED';
    case 'completed':
      return 'COMPLETED';
    default:
      return 'WAITING_RECEPTION';
  }
}

/**
 * 金額情報を計算
 * @param formData フォームデータ
 * @returns 金額情報
 */
export function calculateAmount(formData: { amount?: number; discount?: number; discountReason?: string }): ExtendedReservation['amount'] {
  const subtotal = formData.amount || 0;
  const discount = formData.discount || 0;
  const tax = Math.floor((subtotal - discount) * 0.1); // 10%消費税
  const total = subtotal - discount + tax;
  
  return {
    subtotal,
    tax,
    total,
    discount,
    discountReason: formData.discountReason
  };
}

/**
 * ポイントを計算（1%還元率）
 * @param amount 金額
 * @returns 獲得ポイント
 */
export function calculatePoints(amount: { total: number }): number {
  return Math.floor(amount.total * 0.01);
}

/**
 * 重複顧客を検出
 * @param customers 顧客リスト
 * @returns 重複候補のグループ
 */
export function findDuplicateCustomers(customers: ExtendedCustomer[]): {
  [key: string]: ExtendedCustomer[]
} {
  const duplicates: { [key: string]: ExtendedCustomer[] } = {};
  
  // 電話番号で重複を検出
  const phoneMap = new Map<string, ExtendedCustomer[]>();
  customers.forEach(customer => {
    const phone = customer.phone.replace(/\D/g, ''); // 数字のみに正規化
    if (!phoneMap.has(phone)) {
      phoneMap.set(phone, []);
    }
    phoneMap.get(phone)!.push(customer);
  });
  
  // 重複がある電話番号のみを抽出
  phoneMap.forEach((customers, phone) => {
    if (customers.length > 1) {
      duplicates[`phone_${phone}`] = customers;
    }
  });
  
  // メールアドレスで重複を検出
  const emailMap = new Map<string, ExtendedCustomer[]>();
  customers.forEach(customer => {
    const email = customer.email.toLowerCase();
    if (!emailMap.has(email)) {
      emailMap.set(email, []);
    }
    emailMap.get(email)!.push(customer);
  });
  
  emailMap.forEach((customers, email) => {
    if (customers.length > 1) {
      duplicates[`email_${email}`] = customers;
    }
  });
  
  return duplicates;
}

/**
 * 顧客を統合
 * @param targetId 統合先の顧客ID
 * @param sourceIds 統合元の顧客ID配列
 * @param customers 顧客リスト
 * @returns 統合後の顧客
 */
export function mergeCustomers(
  targetId: number,
  sourceIds: number[],
  customers: ExtendedCustomer[]
): ExtendedCustomer {
  const targetIndex = customers.findIndex(c => c.id === targetId);
  if (targetIndex === -1) {
    throw new Error('統合先の顧客が見つかりません');
  }
  
  const target = customers[targetIndex];
  const sources = customers.filter(c => sourceIds.includes(c.id));
  
  // 統合後の情報を集約
  let totalVisits = target.totalVisits;
  let totalSpent = target.totalSpent;
  let preSystemVisitCount = target.preSystemVisitCount || 0;
  
  sources.forEach(source => {
    totalVisits += source.totalVisits;
    totalSpent += source.totalSpent;
    preSystemVisitCount += source.preSystemVisitCount || 0;
  });
  
  // 統合履歴を更新
  const mergedFrom = target.mergeHistory.mergedFrom || [];
  mergedFrom.push(...sourceIds.map(id => id.toString()));
  
  // 統合後の顧客情報
  const mergedCustomer: ExtendedCustomer = {
    ...target,
    totalVisits,
    totalSpent,
    preSystemVisitCount,
    mergeHistory: {
      ...target.mergeHistory,
      mergedFrom,
      mergedAt: new Date(),
      mergedBy: 'system' // TODO: 実際のユーザー情報を設定
    },
    updatedAt: new Date().toISOString()
  };
  
  return mergedCustomer;
}

/**
 * 日付計算ユーティリティ
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return date1.getFullYear() === date2.getFullYear() &&
         date1.getMonth() === date2.getMonth() &&
         date1.getDate() === date2.getDate();
}

/**
 * 来店処理が期限内かチェック
 * @param visitDate 来店日
 * @returns 期限内ならtrue
 */
export function isWithinVisitProcessingDeadline(visitDate: Date): boolean {
  const now = new Date();
  const deadline = calculateVisitProcessingDeadline(visitDate);
  return now <= deadline;
}

/**
 * 予約の詳細ステータスラベルを取得
 * @param status 詳細ステータス
 * @returns ラベル文字列
 */
export function getDetailedStatusLabel(status: DetailedReservationStatus): string {
  const labels: Record<DetailedReservationStatus, string> = {
    'WAITING_RECEPTION': '受付待ち',
    'RECEPTION_COMPLETED': '受付済み',
    'IN_SERVICE': '施術中',
    'COMPLETED': '済み',
    'CUSTOMER_CANCELLED': 'お客様キャンセル',
    'SALON_CANCELLED': 'サロンキャンセル'
  };
  return labels[status] || status;
}

/**
 * 来店処理ステータスラベルを取得
 * @param status 来店処理ステータス
 * @returns ラベル文字列
 */
export function getVisitProcessingStatusLabel(status: VisitProcessingStatus): string {
  const labels: Record<VisitProcessingStatus, string> = {
    'PENDING': '未処理',
    'AMOUNT_CONFIRMED': '金額確定済み',
    'POINT_CONFIRMED': 'ポイント確定済み'
  };
  return labels[status] || status;
}

// Re-export utility functions from salonboard.ts
export {
  calculateVisitProcessingDeadline,
  calculateCancellationFee
};